// CalculatorShell.tsx — F15b: shared layout for the standalone calculator pages (header,
// D7 disclaimer, CTA to the app, cross links, footer). Mirrors the F15a pilot structure
// (CalculadoraIrpf2025Page keeps its own inline layout to minimize churn); the copy of each
// page arrives via props so the per-page text lives with its page file.

/* eslint-disable gestorpro/no-spanish-jsx --
   Documented F15a/F15b decision (plan §4): standalone Spanish-market SEO pages render outside
   the app shell (no i18n runtime); copy is direct Spanish by design. */

import React from 'react';
import { RelatedCalculators } from './RelatedCalculators';

interface CalculatorShellProps {
    rootId: string;
    /** Page H1 (must match the static fallback H1 of the HTML entry). */
    heading: string;
    intro: string;
    disclaimerId: string;
    /** D7 disclaimer body — the "Borrador orientativo" badge is fixed. */
    disclaimerBody: string;
    ctaId: string;
    ctaHeading: string;
    ctaBody: string;
    /** Canonical path of this page (for the cross-link exclusion). */
    currentPath: string;
    /** Optional honest scope note (e.g. foral regimes) rendered under the main content. */
    scopeNote?: string;
    children: React.ReactNode;
}

export const CalculatorShell: React.FC<CalculatorShellProps> = ({
    rootId,
    heading,
    intro,
    disclaimerId,
    disclaimerBody,
    ctaId,
    ctaHeading,
    ctaBody,
    currentPath,
    scopeNote,
    children,
}) => (
    <div id={rootId} className="min-h-screen bg-background text-foreground font-sans">
        <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12 flex flex-col gap-8">

            {/* Header: brand + link back to the app root */}
            <header className="flex items-center justify-between">
                <a href="/" className="text-lg font-extrabold tracking-tight text-foreground">
                    Gestor<span className="text-primary">Pro</span>
                </a>
                <a href="/" className="text-xs font-bold text-muted-foreground hover:text-foreground transition-colors">
                    Ir a la app →
                </a>
            </header>

            <main className="flex flex-col gap-6">
                <section className="space-y-3">
                    <h1 className="type-view-title text-3xl">{heading}</h1>
                    <p className="text-sm text-muted-foreground leading-relaxed">{intro}</p>
                </section>

                {/* D7 — the draft disclaimer must be VISIBLE, not fine print */}
                <div
                    id={disclaimerId}
                    className="bg-warning/10 border border-warning rounded-xl p-4 flex flex-col gap-1.5"
                >
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-warning-foreground bg-warning px-2 py-0.5 rounded-full self-start">
                        Borrador orientativo
                    </span>
                    <p className="text-xs text-foreground leading-relaxed">{disclaimerBody}</p>
                </div>

                {children}

                {scopeNote && (
                    <p className="text-[11px] text-muted-foreground leading-relaxed bg-muted/40 border border-border p-3 rounded-xl">
                        {scopeNote}
                    </p>
                )}

                {/* CTA to the app (F15a gap documented: no URL-triggered demo route — CTA points to root). */}
                <section className="bg-card border border-border rounded-xl shadow-e1 p-5 sm:p-6 flex flex-col gap-3">
                    <h2 className="text-lg font-extrabold tracking-tight">{ctaHeading}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">{ctaBody}</p>
                    <a
                        id={ctaId}
                        href="/"
                        className="self-start bg-primary text-primary-foreground text-sm font-extrabold px-5 py-2.5 rounded-xl shadow-e1 hover:opacity-90 transition-opacity"
                    >
                        Pruébalo gratis con tus datos
                    </a>
                </section>

                <RelatedCalculators currentPath={currentPath} />
            </main>

            <footer className="border-t border-border pt-4 flex flex-col gap-1.5">
                <p className="text-[11px] text-muted-foreground">
                    <a href="/" className="font-bold hover:text-foreground transition-colors">GestorPro</a>
                    {' '}— Entiende tu dinero. Sin trackers y con tus datos en la UE.
                </p>
                <p className="text-[11px] text-muted-foreground">
                    Esta calculadora es un borrador orientativo y no constituye asesoramiento fiscal.
                </p>
            </footer>
        </div>
    </div>
);
